import { AxiosError } from 'axios'
import { createContext, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { loginRedirectUrl } from '~/configs/app'
import { SUCCESS_MESSAGES } from '~/constants/messages'
import useHandleError from '~/hooks/useHandleError'
import auth from '~/services/auth'
import type { AuthContextProps, ProfileData } from '~/types/auth'
import type { DefaultApiResponse } from '~/types/default'
import type { LayoutProps } from '~/types/layout'
import { removeTokens, saveTokens } from '~/utils/token'

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isLoading: true,
  profile: null,
  doLogin: () => {},
  doLogout: () => {},
  doRefresh: () => {},
  checkAuthentication: () => {},
})

const AuthProvider = ({ children }: LayoutProps) => {
  const handleError = useHandleError()
  const navigate = useNavigate()

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [profile, setProfile] = useState<ProfileData | null>(null)

  const doLogin = async (token: string) => {
    setIsLoading(true)
    try {
      const response = await auth.doLogin(token)
      setIsAuthenticated(true)
      saveTokens(response)
      toast.success(SUCCESS_MESSAGES.SUCCESS_LOGIN)
    } catch (error) {
      handleError(error as AxiosError<DefaultApiResponse>)
      window.location.replace(loginRedirectUrl)
    } finally {
      setIsLoading(false)
    }
  }

  const doLogout = async () => {
    setIsLoading(true)
    try {
      await auth.doLogout()
      setIsAuthenticated(false)
      removeTokens()
      toast.success(SUCCESS_MESSAGES.SUCCESS_LOGOUT)
      window.location.replace(loginRedirectUrl)
    } catch (error) {
      handleError(error as AxiosError<DefaultApiResponse>)
      window.location.replace(loginRedirectUrl)
    } finally {
      setIsLoading(false)
    }
  }

  const doRefresh = async () => {
    setIsLoading(true)
    try {
      const response = await auth.doRefresh()
      setIsAuthenticated(true)
      saveTokens(response.data.token)
    } catch (error) {
      handleError(error as AxiosError<DefaultApiResponse>)
      window.location.replace(loginRedirectUrl)
    } finally {
      setIsLoading(false)
    }
  }

  const checkAuthentication = async () => {
    setIsLoading(true)
    try {
      const profileResponse = await auth.getProfile()
      setProfile(profileResponse.data)
      setIsAuthenticated(true)
    } catch (error) {
      handleError(error as AxiosError<DefaultApiResponse>)
      window.location.replace(loginRedirectUrl)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, profile, doLogin, doLogout, doRefresh, checkAuthentication }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
